import {Image, Platform} from 'react-native';
import {makeAutoObservable, runInAction} from 'mobx';

import config from '../config';
import GiftCardsIconImg from '../images/brand/icons/category/gift-cards.png';
import SpecialsIconImg from '../images/brand/icons/category/specials.png';
import {Department} from '../models/middleware';
import apiService, {GetDepartmentLinesRequestParams} from '../transport/apiService';
import {OrderLine} from '../transport/apiServiceResponse';
import {logger, serializeError} from '../utils/logger';

import type {RootStore} from './rootStore';

interface DepartmentLinesMeta {
  page: number;
  page_count: number;
  total_count?: number;
}

interface DepartmentLinesResponse {
  data: OrderLine[];
  meta: DepartmentLinesMeta;
}

interface SortOption {
  id: string;
  name: string;
}

interface FilterOption {
  query_value: string;
  [key: string]: unknown;
}

const specialsIconUri =
  Platform.OS === 'web' ? SpecialsIconImg : Image.resolveAssetSource(SpecialsIconImg).uri;

const giftCardsIconUri =
  Platform.OS === 'web' ? GiftCardsIconImg : Image.resolveAssetSource(GiftCardsIconImg).uri;

export const SPECIALS_TILE = 'SPECIALS_TILE';
export const GIFT_CARDS_TILE = 'GIFT_CARDS_TILE';

export class RangeStore {
  rootStore: RootStore;
  // Department selection
  parentDepartment: Department | null = null;
  departments: Department[] = [];
  selectedDepartment: Department | null = null;
  selectedSubDepartment: Department | null = null;
  // Range product list
  departmentLines: OrderLine[] = [];
  departmentLinesMeta: DepartmentLinesMeta = {page: 0, page_count: 0};
  // Filter & Sort options
  selectedSortOption: SortOption | null = null;
  selectedFilterOptions: FilterOption[] | null = null;
  // Loading flags
  isLoadingDepartmentLines = false;
  isLoadingNextPage = false;

  // This is a specials tile
  specialsDepartment: Department = {
    id: SPECIALS_TILE,
    type: 'departments',
    name: 'Specials',
    parent: null,
    position: -5,
    promote_at_checkout: false,
    links: {
      icon: specialsIconUri,
    },
  };

  giftCardsDepartment: Department = {
    id: GIFT_CARDS_TILE,
    type: 'departments',
    name: 'Gift Cards',
    parent: null,
    position: -4,
    promote_at_checkout: false,
    links: {
      icon: giftCardsIconUri,
    },
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  reset = () => {
    this.parentDepartment = null;
    this.departments = [];
    this.selectedDepartment = null;
    this.departmentLines = [];
    this.departmentLinesMeta = {page: 0, page_count: 0};
    this.selectedSubDepartment = null;
    this.selectedSortOption = null;
    this.selectedFilterOptions = null;
  };

  get sortedAndFilteredDepartments() {
    return this.departments
      .slice()
      .sort((a: Department, b: Department) => a.position - b.position)
      .filter((item: Department) => item.parent?.id === this.parentDepartment?.id);
  }

  // Keep filterDepartmentsByParent for potential future use with parameters
  filterDepartmentsByParent = (parentId?: string) =>
    this.departments
      .slice()
      .sort((a: Department, b: Department) => a.position - b.position)
      .filter((item: Department) => item.parent?.id === (parentId || this.parentDepartment?.id));

  loadDepartments = async () => {
    try {
      const departments = await apiService.getDepartments();
      const parentDept = departments.filter((item: Department) => item.parent === null)[0];
      const specials = {
        ...this.specialsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const giftCards = {
        ...this.giftCardsDepartment,
        parent: {
          id: parentDept.id,
        },
      };
      const finalDepartments = [...departments, specials];
      if (config.FF_IS_GIFT_CARDS_ENABLED) {
        finalDepartments.push(giftCards);
      }
      runInAction(() => {
        this.parentDepartment = parentDept;
        // @ts-expect-error - Type mismatch needs investigation
        this.departments = finalDepartments;
      });
    } catch (err) {
      logger.error('RangeStore', 'Error loading departments', serializeError(err));
    }
  };

  selectDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  selectSubDepartment = (department: Department) => {
    runInAction(() => {
      this.selectedSubDepartment = department;
    });
    if (department.id === SPECIALS_TILE) {
      apiService.setDepartment({specials: 1});
    } else {
      apiService.setDepartment({department_id: department.id});
    }
  };

  getSubDepartments = (departmentId?: string) =>
    this.filterDepartmentsByParent(departmentId || this.selectedDepartment?.id);

  loadRangeLines = async () => {
    runInAction(() => {
      this.isLoadingDepartmentLines = true;
      this.departmentLines = [];
    });
    // sort options
    const options: GetDepartmentLinesRequestParams = {};
    if (this.selectedSortOption) {
      options.sort_option_id = this.selectedSortOption.id;
    }
    // filter options
    if (this.selectedFilterOptions) {
      options.facets = this.selectedFilterOptions;
    }

    try {
      const data = (await apiService.getDepartmentLines(options)) as DepartmentLinesResponse;
      runInAction(() => {
        this.departmentLines = data.data;
        this.departmentLinesMeta = data.meta;
      });
    } catch (err) {
      logger.error('RangeStore', 'Error loading lines', serializeError(err));
    }

    runInAction(() => {
      this.isLoadingDepartmentLines = false;
    });
  };

  loadNextPageRangeLines = () => {
    if (this.isLoadingNextPage) {
      return;
    }
    if (this.departmentLinesMeta.page < this.departmentLinesMeta.page_count) {
      this.isLoadingNextPage = true;
      const options: GetDepartmentLinesRequestParams = {};
      if (this.selectedSortOption) {
        options.sort_option_id = this.selectedSortOption.id;
      }
      if (this.selectedFilterOptions) {
        options.facets = this.selectedFilterOptions;
      }
      options.page = this.departmentLinesMeta.page + 1;
      apiService
        .getDepartmentLines(options)
        .then((data: unknown) => {
          const response = data as DepartmentLinesResponse;
          runInAction(() => {
            this.departmentLines = [...this.departmentLines, ...response.data];
            this.departmentLinesMeta = response.meta;
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
        })
        .catch(err => {
          runInAction(() => {
            this.isLoadingDepartmentLines = false;
            this.isLoadingNextPage = false;
          });
          logger.error('RangeStore', 'Error loading next page', serializeError(err));
        });
    }
  };

  setSelectedSortOption = (option: SortOption | null) => (this.selectedSortOption = option);

  setSelectedFilterOptions = (filters: FilterOption[] | null) =>
    (this.selectedFilterOptions = filters);

  clearSelectedFilterOptions = () => (this.selectedFilterOptions = null);

  clearSelectedSortAndFilter = (defaultSortOption: SortOption | null) =>
    runInAction(() => {
      this.selectedSortOption = defaultSortOption;
      this.selectedFilterOptions = null;
    });
}
